import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { TrustBadges, TrustLevel } from "@/components/TrustBadges";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bookmark, ShieldCheck, LogOut, Phone, BadgeCheck, Briefcase, ChevronRight, Check, LayoutDashboard, DoorClosed } from "lucide-react";

export default function Profile() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  useEffect(() => { api.get("/listings/mine").then((r) => setListings(r.data)).catch(() => {}); }, []);

  const p = user?.profile || {};
  const v = user?.verification || {};

  const doVerify = async (kind) => {
    try {
      const { data } = await api.post("/verify", { kind });
      updateUser({ ...user, verification: data.verification, trust_level: data.trust_level });
      toast.success("Verified! ✓");
    } catch { toast.error("Could not verify"); }
  };

  const rows = [
    { on: v.phone_verified, icon: Phone, label: "Phone verified", kind: "phone" },
    { on: v.identity_verified, icon: BadgeCheck, label: "Identity verified", kind: "identity" },
    { on: v.professional_verified, icon: Briefcase, label: "Professional verified", kind: "professional" },
  ];

  return (
    <div>
      <TopBar greeting="Your account" name="Profile" />
      <div className="space-y-6 px-4 pt-4">
        <div className="flex items-center gap-4 rounded-3xl bg-white p-4 shadow-card">
          <img src={resolveImg(p.photo_url) || "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=200"} alt="" className="h-20 w-20 rounded-2xl object-cover" />
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold text-ink">{p.first_name} {p.last_name}</h2>
            <p className="text-sm text-ink-soft">{p.occupation || user?.email}</p>
            <div className="mt-2"><TrustLevel level={user?.trust_level} /></div>
          </div>
        </div>

        {!p.photo_url && (
          <button data-testid="complete-profile-banner" onClick={() => navigate("/onboarding")} className="flex w-full items-center gap-3 rounded-3xl bg-brand-light p-4 text-left">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand text-white"><ChevronRight className="h-5 w-5" /></div>
            <div className="flex-1"><h4 className="font-heading font-bold text-ink">Add a profile photo</h4><p className="text-sm text-ink-soft">Help your future flatmates recognise you — upload a photo or pick an avatar.</p></div>
          </button>
        )}

        <div className="rounded-3xl bg-white p-5 shadow-card">
          <div className="mb-3 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand" /><h3 className="font-heading text-lg font-bold text-ink">Trust & verification</h3></div>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.kind} className="flex items-center gap-3 rounded-2xl bg-secondary p-3">
                <div className={`grid h-9 w-9 place-items-center rounded-xl ${r.on ? "bg-mint/15 text-mint" : "bg-white text-ink-soft"}`}><r.icon className="h-4 w-4" /></div>
                <span className="flex-1 text-sm font-semibold text-ink">{r.label}</span>
                {r.on ? <span className="flex items-center gap-1 text-xs font-bold text-mint"><Check className="h-4 w-4" /> Done</span>
                  : <button data-testid={`verify-${r.kind}`} onClick={() => doVerify(r.kind)} className="rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white">Verify</button>}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-ink-soft">Documents are never shown publicly. Verification only powers your trust badges.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button data-testid="profile-saved" onClick={() => navigate("/saved")} className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-soft">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-light"><Bookmark className="h-5 w-5 text-brand" /></div>
            <span className="font-semibold text-ink">Saved</span>
          </button>
          <button data-testid="profile-edit" onClick={() => navigate("/onboarding")} className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-soft">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-secondary"><ChevronRight className="h-5 w-5 text-ink" /></div>
            <span className="font-semibold text-ink">Edit profile</span>
          </button>
        </div>

        {user?.role === "admin" && (
          <button data-testid="profile-admin" onClick={() => navigate("/admin")} className="flex w-full items-center gap-3 rounded-3xl bg-ink p-4 text-white shadow-card">
            <LayoutDashboard className="h-5 w-5" /><span className="flex-1 text-left font-bold">Admin dashboard</span><ChevronRight className="h-5 w-5" />
          </button>
        )}

        <div>
          <h3 className="mb-3 font-heading text-lg font-bold text-ink">Your listings</h3>
          {listings.length ? (
            <div className="space-y-3">
              {listings.map((l) => (
                <div key={l.listing_id} onClick={() => l.status !== "draft" && navigate(`/listing/${l.listing_id}`)} className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-soft">
                  <img src={resolveImg(l.images?.[0]) || "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=200"} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                  <div className="flex-1"><h4 className="line-clamp-1 font-semibold text-ink">{l.title}</h4><p className="text-xs text-ink-soft">{l.locality} · {l.status}</p></div>
                </div>
              ))}
            </div>
          ) : <p className="rounded-3xl bg-white p-5 text-center text-sm text-ink-soft shadow-soft">No listings yet. Tap Post to create one.</p>}
        </div>

        <Button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }} variant="outline" className="h-12 w-full rounded-full border-border font-bold text-ink-soft">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}
